import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MapPin } from "lucide-react";
import { LineReveal, Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { Burst } from "@/components/Burst";
import { fetchMenu } from "@/lib/api";

const FILTERS = ["All", "Sweet Stuff", "Savory Stuff", "Drinks"];
const CARD_COLORS = ["bg-cb-yellow", "bg-cb-teal", "bg-cb-pink text-white", "bg-white", "bg-cb-yellow"];
const TILTS = ["-rotate-1", "rotate-1", "-rotate-2", "rotate-2", "rotate-0"];

export default function Menu() {
  const { data: items = [], isLoading } = useQuery({ queryKey: ["menu"], queryFn: fetchMenu });
  const [filter, setFilter] = useState("All");

  const visible = filter === "All" ? items : items.filter((i) => i.category === filter);

  return (
    <div data-testid="menu-page">
      <section className="bg-cb-pink border-b-3 border-cb-black py-16 sm:py-24 relative overflow-hidden">
        <div className="hidden md:block absolute -top-16 -left-16 w-64 h-64 bg-cb-yellow border-3 border-cb-black opacity-70 animate-spin-slow" style={{ borderRadius: "58% 42% 45% 55% / 50% 58% 42% 50%" }} />
        <div className="max-w-7xl mx-auto px-4 text-center relative">
          <span className="cb-badge bg-white text-cb-black mb-6 inline-block" data-testid="menu-badge">Fresh · Hot · Delicious</span>
          <h1 className="font-display font-bold uppercase leading-[0.95] tracking-tight text-4xl sm:text-5xl lg:text-6xl" data-testid="menu-headline">
            <LineReveal
              lines={[
                <span key="a" className="text-outline-yellow inline-block -rotate-1">The Crazy</span>,
                <span key="b" className="text-outline-white inline-block rotate-1">Menu</span>,
              ]}
            />
          </h1>
          <Reveal delay={0.55}>
            <p className="mt-5 text-white font-bold text-base md:text-lg max-w-xl mx-auto">
              Small menu, huge flavor. Everything made fresh in the trailer, every single morning.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-cb-cream halftone-light py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal className="flex flex-wrap justify-center gap-3 mb-12">
            {FILTERS.map((f) => (
              <button
                key={f}
                data-testid={`menu-filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setFilter(f)}
                className={`cb-btn !px-5 !py-2 !text-xs sm:!text-sm ${filter === f ? "cb-btn-black" : "cb-btn-white"}`}
              >
                {f}
              </button>
            ))}
          </Reveal>

          {isLoading ? (
            <p className="text-center font-display font-semibold uppercase text-lg animate-pulse" data-testid="menu-loading">Warming up the oven...</p>
          ) : (
            <div className="grid gap-10 sm:gap-8 sm:grid-cols-2 lg:grid-cols-3" data-testid="menu-grid">
              {visible.map((item, i) => (
                <Reveal key={item.slug} delay={i * 0.08}>
                  <article
                    data-testid={`menu-item-${item.slug}`}
                    className={`cb-card cb-card-hover overflow-hidden ${TILTS[i % TILTS.length]} hover:rotate-0 h-full flex flex-col`}
                  >
                    <div className={`relative border-b-3 border-cb-black aspect-[4/3] overflow-hidden ${CARD_COLORS[i % CARD_COLORS.length]}`}>
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                      <span className="cb-badge bg-cb-black text-cb-yellow absolute top-4 left-4">{item.tag}</span>
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <h2 className="font-display font-semibold uppercase text-xl sm:text-2xl leading-tight">{item.name}</h2>
                        <span className="font-display font-bold text-2xl text-cb-pink shrink-0">{item.price}</span>
                      </div>
                      <span className="mt-1 text-xs font-extrabold uppercase tracking-wide text-cb-black/50">{item.price_note}</span>
                      <p className="mt-3 font-bold text-cb-black/60 text-sm flex-1">{item.desc}</p>
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="bg-cb-yellow border-y-3 border-cb-black py-16 sm:py-24 relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <h2 className="font-display font-bold uppercase text-2xl sm:text-3xl lg:text-4xl tracking-tight" data-testid="menu-board-heading">
              The Real Deal <span className="text-cb-pink">Board</span>
            </h2>
            <p className="mt-4 font-bold text-cb-black/70 text-base md:text-lg leading-relaxed">
              This is the exact board hanging on the trailer. No tricks, no tiny print — just big buns and honest prices.
            </p>
            <Link to="/truck" data-testid="menu-find-truck-btn" className="cb-btn cb-btn-black mt-7">
              <MapPin className="w-5 h-5" /> Find The Truck & Order
            </Link>
          </Reveal>
          <Reveal delay={0.15} className="relative">
            <div className="cb-card overflow-hidden -rotate-2 hover:rotate-0 transition-transform duration-300">
              <img src="/menu-board.jpg" alt="Crazy Buns menu board" className="w-full" data-testid="menu-board-image" />
            </div>
            <Burst text="4 For $15" subtext="Roll Deal" color="#FF1493" textColor="#FFFFFF" className="absolute -top-6 -right-4 w-28 sm:w-36 animate-wiggle" rotate="rotate-6" />
          </Reveal>
        </div>
      </section>

      <Marquee bg="bg-cb-teal" text="text-cb-black" />
    </div>
  );
}
